import { JsonValue } from '@execonline-inc/decoders';
import { toUrl } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Kettle } from '@execonline-inc/kettle.private';
import { Maybe, just, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { error } from '../ErrorHandling';
import { findLinkBy } from '../Links';
import { findLink } from '../LinkyLinky';
import { FlashAlert, errorAlert } from '../Notifications/Types';
import { Link } from '../Resource/Types';
import { TPlainTextKey } from '../Translations';
import {
  FormFieldAssetResource,
  FormFieldOutput,
} from '../components/EmbeddedFormFieldAsset/Types';
import {
  Advancer,
  AnyAdvancer,
  AttachmentResource,
  CompletabilityValue,
  CompleteAndAdvancer,
  ConfirmationModal,
  ModulePresentationStyle,
  QuizResult,
  SegmentFormFieldOutput,
  SegmentResource,
  SegmentState,
  SegmentSubmissionParams,
  UponCompletion,
  advancer,
  advancerForResource,
  advancing,
  advancingTo,
  completeAndAdvancer,
  completing,
  completingAndAdvancing,
  loaded,
  loading,
  noAdvancer,
  previousAdvancing,
  processingRequest,
  quizFailed,
  ready,
  reportingResults,
  scheduleSession,
  submitAndAdvancer,
  submittingAndAdvancing,
  waiting,
} from './Types';
import { urlsEq } from './Url';

const segmentCompletability = (
  completability: CompletabilityValue,
  segmentStore: SegmentStore,
): Maybe<CompleteAndAdvancer> => {
  switch (completability) {
    case 'completable':
      return just(completeAndAdvancer(segmentStore));
    case 'not-completable':
      return nothing();
  }
};

const segmentTypeCompleteAndAdvancer = (segmentStore: SegmentStore): Maybe<CompleteAndAdvancer> => {
  return segmentStore.segmentResource.andThen((segmentResource) => {
    switch (segmentResource.payload.type) {
      case 'overview':
      case 'presentation':
        return segmentResource.payload.status.cata({
          Just: (status) =>
            status === 'Complete' ? nothing() : just(completeAndAdvancer(segmentStore)),
          Nothing: () => just(completeAndAdvancer(segmentStore)),
        });
      case 'external-program':
      case 'scorm': {
        return segmentCompletability(segmentResource.payload.completability, segmentStore);
      }
      case 'team-discussion': {
        return segmentResource.payload.teamDiscussion.andThen(() =>
          segmentResource.payload.status.cata({
            Just: (status) =>
              status !== 'Complete' ? just(completeAndAdvancer(segmentStore)) : nothing(),
            Nothing: () => just(completeAndAdvancer(segmentStore)),
          }),
        );
      }
      case 'lecture':
      case 'survey':
      case 'assignment-due':
        return nothing();
    }
  });
};

const segmentComplete = (segmentResource: SegmentResource): Maybe<string> => {
  return segmentResource.payload.status.cata({
    Just: (status) => (status === 'Complete' ? just(status) : nothing()),
    Nothing: () => nothing(),
  });
};

const overviewSegment = (segmentResource: SegmentResource): boolean => {
  return segmentResource.payload.type == 'overview';
};

class SegmentStore {
  @observable
  state: SegmentState;

  @observable
  kettle: Kettle;

  @observable
  embeddedFormFieldOutputs: SegmentFormFieldOutput[];

  @observable
  presentationStyle: ModulePresentationStyle;

  // Set when the learner moved on from a segment but the course has no next segment
  // open to them yet (the next module is gated, or the course is finished). The full
  // segment page uses it to keep the learner in the course and say what is locked.
  @observable
  nextUnavailable: boolean = false;

  // Set when the learner opened a segment (from the dashboard or a link) that is not open
  // to them yet: its module is gated, or the program has not started. The full segment
  // page uses it to keep the learner in the course and say when the module opens.
  @observable
  segmentUnavailable: boolean = false;

  // Set with either flag above when the server refused the segment with a 403. If the
  // program data does not say what is locked, the page still keeps the learner in the
  // course and shows that the segment is not open to them.
  @observable
  segmentRefused: boolean = false;

  enclosureLink = computed(() => findLink('enclosure', this.links), {
    equals: urlsEq,
  });

  constructor(presentationStyle: ModulePresentationStyle) {
    makeObservable(this);

    this.state = waiting();
    this.kettle = new Kettle();
    this.embeddedFormFieldOutputs = [];
    this.presentationStyle = presentationStyle;
  }

  @action
  loading = (link: Link) => {
    this.nextUnavailable = false;
    this.segmentUnavailable = false;
    this.segmentRefused = false;
    this.state = loading(link);
  };

  // The learner finished this segment and there is no next segment to go to. Stay on
  // the (now updated) segment and flag it, rather than leaving the course.
  @action
  reachedEndOfAvailableSegments = (segmentResource: SegmentResource, refused: boolean) => {
    this.nextUnavailable = true;
    this.segmentRefused = refused;
    this.ready(segmentResource);
  };

  // The segment the learner opened is not open to them yet. There is no segment to show,
  // so the store stays where it is and the page explains what is locked.
  @action
  openedUnavailableSegment = (refused: boolean) => {
    this.segmentUnavailable = true;
    this.segmentRefused = refused;
  };

  // Jump to an arbitrary segment (e.g. from the Overview outline). Mirrors the
  // per-segment reset that advanceTo/previousAdvance do, so stale embedded form
  // field outputs from the previous segment don't leak into allFieldsAreComplete
  // or a subsequent submit. The 'loading' state is then fetched by SegmentReactions.
  @action
  navigateToSegment = (link: Link) => {
    this.embeddedFormFieldOutputs = [];
    this.nextUnavailable = false;
    this.segmentUnavailable = false;
    this.segmentRefused = false;
    this.state = loading(link);
  };

  // True exactly when the Next Segment button is enabled for the loaded segment:
  // plain/complete advancers are always enabled; a submit advancer (forms/overview)
  // is enabled once every required field is valid. Drives the Overview's completed
  // icon so it flips at the same moment Next becomes active.
  @computed
  get nextAdvanceActive(): boolean {
    const inReadyState =
      this.state.kind === 'ready' ||
      this.state.kind === 'processing-request' ||
      this.state.kind === 'schedule-session';
    if (!inReadyState) {
      return false;
    }
    switch (this.segmentAdvancer.kind) {
      case 'advancer':
      case 'complete-and-advancer':
        return true;
      case 'submit-and-advancer':
        // Mirror SubmitAndAdvanceButton: the button only shows when showCompletionCta
        // and only enables once every required field is valid.
        return this.showCompletionCta && this.allFieldsAreComplete;
      case 'disabled-advancer':
      case 'no-advancer':
        return false;
    }
  }

  @action
  waiting = () => {
    this.state = waiting();
  };

  @action
  ready = (segmentResource: SegmentResource) => {
    this.state = ready(segmentResource);
    this.presentationStyle = segmentResource.payload.presentationStyle;
  };

  @action
  loaded = (segmentResource: SegmentResource) => {
    this.nextUnavailable = false;
    this.segmentUnavailable = false;
    this.segmentRefused = false;
    this.state = loaded(segmentResource);
  };

  // A background save finished. Its ONLY legitimate effect on segment state
  // is to retire the transient state that save put up:
  //   'processing-request'  -- the "Saving..." indicator a DCE dropdown sets
  //                            in +DropdownSelect#handleUpdate+
  //   'reporting-results'   -- a presentation posting results from its iframe
  // From anything else, and in particular from any navigation state, it does
  // nothing at all.
  //
  // Both call sites used to announce +store.segmentResource.map(store.ready)+
  // unconditionally. A save reply that landed while the learner was advancing
  // knocked the store out of 'submitting-and-advancing' /
  // 'completing-and-advancing', with two consequences: +SegmentContent+
  // swapped its spinner back for the content, remounting the subtree and
  // repainting a DCE from the page-load results snapshot (so the learner's
  // answers vanished) or reloading a presentation iframe from the start; and
  // the advance that followed found a state +advanceTo+ does not accept, so
  // Next Segment silently did nothing (BUG-570).
  //
  // Reached on failure as well as success: a segment that entered
  // 'processing-request' would otherwise sit there forever behind a disabled
  // "Saving..." button whenever the save timed out.
  @action
  saveComplete = () => {
    switch (this.state.kind) {
      case 'processing-request':
      case 'reporting-results': {
        this.state = ready(this.state.segmentResource);
        break;
      }
      case 'ready':
      case 'quiz-failed':
      case 'schedule-session':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(this.state);
    }
  };

  // Swap a freshly fetched resource in without changing what the segment is
  // doing. SCORM refetches the resource when a terminal commit lands, to flip
  // +payload.completability+ and reveal the Next Segment button without a page
  // refresh; that refetch used to call +ready+ directly, which is the same
  // unconditional stomp described on +saveComplete+ -- and worse here, because
  // the terminal commit fires exactly when the learner turns to click Next, so
  // it needs no timing accident to collide (BUG-570).
  //
  // Only 'ready' and 'processing-request' take the swap. In any navigation
  // state the learner is already leaving, so the refreshed completability is
  // moot and replacing the resource would only disturb the transition.
  @action
  refreshSegmentResource = (segmentResource: SegmentResource) => {
    switch (this.state.kind) {
      case 'ready': {
        this.state = ready(segmentResource);
        this.presentationStyle = segmentResource.payload.presentationStyle;
        break;
      }
      case 'processing-request': {
        this.state = processingRequest(segmentResource);
        break;
      }
      case 'quiz-failed':
      case 'reporting-results':
      case 'schedule-session':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(this.state);
    }
  };

  // Fold what the autosave just persisted back into the loaded resource, so
  // +payload.results+ tracks the server instead of staying frozen at whatever
  // was fetched when the segment opened.
  //
  // Field components seed themselves from +segmentStore.results+ on mount
  // (+useFormField+, +RadioFieldReactions+, +DropdownSelectReactions+). While
  // that snapshot stayed stale, any remount repainted the form empty AND
  // re-registered every field in +embeddedFormFieldOutputs+ with an empty
  // value. The next keystroke then autosaved that set, and because
  // +Courses::UserSegments::ResultsController#create+ assigns
  // +user_segment.results+ wholesale, the learner's already-saved answers were
  // replaced with blanks server-side -- which is why they had to redo a
  // section "right from the beginning" rather than merely retype the screen.
  //
  // Mirrors the payload shape +quizFailedAction+ writes and +getFieldValue+
  // reads.
  @action
  recordSavedResults = (fields: ReadonlyArray<FormFieldOutput>) => {
    this.segmentResource.do((segmentResource) => {
      switch (segmentResource.payload.type) {
        case 'overview':
          segmentResource.payload.results = {
            kind: 'json-value',
            value: fields.map(({ id, value }) => ({ id, value })),
          };
          break;
        case 'presentation':
        case 'assignment-due':
        case 'team-discussion':
        case 'lecture':
        case 'survey':
        case 'external-program':
        case 'scorm':
          break;
        default:
          assertNever(segmentResource.payload);
      }
    });
  };

  @action
  complete = () => {
    switch (this.state.kind) {
      case 'reporting-results':
      case 'ready': {
        this.state = completing(this.state.segmentResource);
        break;
      }
      case 'quiz-failed':
      case 'processing-request':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
      case 'schedule-session':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  advance = () => {
    switch (this.state.kind) {
      case 'reporting-results':
      case 'schedule-session':
      case 'ready': {
        this.state = advancing(this.state.segmentResource);
        break;
      }
      case 'quiz-failed':
      case 'processing-request':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  advanceTo = (link: Link) => {
    switch (this.state.kind) {
      case 'reporting-results':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
        this.embeddedFormFieldOutputs = [];
        this.state = advancingTo(this.state.segmentResource, link);
        break;
      case 'quiz-failed':
      case 'ready':
      case 'processing-request':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'previous-advancing':
      case 'advancing-to':
      case 'schedule-session':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  previousAdvance = () => {
    switch (this.state.kind) {
      case 'reporting-results':
      case 'schedule-session':
      case 'ready': {
        this.embeddedFormFieldOutputs = [];
        this.nextUnavailable = false;
        this.segmentRefused = false;
        this.state = previousAdvancing(this.state.segmentResource);
        break;
      }
      case 'quiz-failed':
      case 'processing-request':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  completeAndAdvance = () => {
    switch (this.state.kind) {
      case 'reporting-results':
      case 'schedule-session':
      case 'ready': {
        this.state = completingAndAdvancing(this.state.segmentResource);
        break;
      }
      case 'quiz-failed':
      case 'processing-request':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submitAndAdvance = (submissionParams: SegmentSubmissionParams) => {
    const state = this.state;
    switch (state.kind) {
      case 'reporting-results':
      case 'schedule-session':
      case 'ready':
        this.state = submittingAndAdvancing(state.segmentResource, submissionParams);
        break;
      case 'quiz-failed':
      case 'processing-request':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(state);
    }
  };

  @action
  updateResults = (results: JsonValue) => {
    switch (this.state.kind) {
      case 'ready': {
        const segmentResource = this.state.segmentResource;
        switch (segmentResource.payload.type) {
          case 'presentation':
            segmentResource.payload.results = results;
            this.state = reportingResults(segmentResource);
            break;
          case 'overview':
          case 'assignment-due':
          case 'team-discussion':
          case 'lecture':
          case 'survey':
          case 'external-program':
          case 'scorm': {
            break;
          }
          default:
            assertNever(segmentResource.payload);
        }
        break;
      }
      case 'quiz-failed':
      case 'processing-request':
      case 'reporting-results':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
      case 'schedule-session':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  refreshKettle = () => {
    switch (this.state.kind) {
      case 'loaded': {
        this.kettle = new Kettle();
        break;
      }
      case 'quiz-failed':
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'processing-request':
      case 'completing':
      case 'advancing':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
      case 'reporting-results':
      case 'schedule-session':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  // 'processing-request' is accepted alongside 'ready' because a dropdown
  // puts the whole segment into it while its own autosave is in flight
  // (DropdownSelect#handleUpdate). Dropping outputs in that window meant a
  // learner could type into a text field, watch the text stay on screen,
  // and have it silently missing from the submission (BUG-570).
  @action
  addEmbeddedFormFieldOutput = (formFieldOutput: SegmentFormFieldOutput) => {
    switch (this.state.kind) {
      case 'ready':
      case 'processing-request': {
        const outputs = this.embeddedFormFieldOutputs
          .filter((store) => store.id !== formFieldOutput.id)
          .concat(formFieldOutput);
        this.embeddedFormFieldOutputs = outputs;
        break;
      }
      case 'quiz-failed':
      case 'reporting-results':
      case 'schedule-session':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  processRequest = () => {
    switch (this.state.kind) {
      case 'ready':
        this.state = processingRequest(this.state.segmentResource);
        break;
      case 'quiz-failed':
      case 'reporting-results':
      case 'processing-request':
      case 'schedule-session':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  scheduleSession = () => {
    switch (this.state.kind) {
      case 'ready': {
        this.state = scheduleSession(this.state);
        break;
      }
      case 'quiz-failed':
      case 'reporting-results':
      case 'processing-request':
      case 'schedule-session':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  quizFailedAction = (segmentResource: SegmentResource, quizResult: QuizResult) => {
    if (segmentResource.payload.type === 'overview' && this.embeddedFormFieldOutputs.length > 0) {
      segmentResource.payload.results = {
        kind: 'json-value',
        value: this.embeddedFormFieldOutputs.map(({ id, value }) => ({ id, value })),
      };
    }
    this.state = quizFailed(segmentResource, quizResult);
  };

  @action
  retryQuiz = () => {
    switch (this.state.kind) {
      case 'quiz-failed': {
        this.embeddedFormFieldOutputs = [];
        this.state = ready(this.state.segmentResource);
        break;
      }
      case 'ready':
      case 'reporting-results':
      case 'schedule-session':
      case 'processing-request':
      case 'waiting':
      case 'loading':
      case 'error':
      case 'completing':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (msg: TPlainTextKey) => {
    this.state = error(msg);
  };

  @computed
  get notification(): Maybe<FlashAlert> {
    switch (this.state.kind) {
      case 'error':
        return just(errorAlert(this.state));
      case 'quiz-failed':
      case 'loaded':
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'completing':
      case 'advancing':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
      case 'reporting-results':
      case 'processing-request':
      case 'schedule-session':
        return nothing();
    }
  }

  @computed
  get segmentResource(): Maybe<SegmentResource> {
    switch (this.state.kind) {
      case 'quiz-failed':
      case 'schedule-session':
      case 'reporting-results':
      case 'ready':
      case 'processing-request':
      case 'advancing':
      case 'loaded':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing-to':
      case 'completing': {
        return just(this.state.segmentResource);
      }
      case 'waiting':
      case 'loading':
      case 'error':
        return nothing();
    }
  }

  @computed
  get title(): Maybe<string> {
    switch (this.state.kind) {
      case 'quiz-failed':
      case 'schedule-session':
      case 'reporting-results':
      case 'ready':
      case 'processing-request':
      case 'loaded':
      case 'completing':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing': {
        return just(this.state.segmentResource.payload.title);
      }
      case 'advancing':
      case 'advancing-to':
      case 'waiting':
      case 'loading':
      case 'error':
        return nothing();
    }
  }

  @computed
  get showCompletionCta(): boolean {
    switch (this.state.kind) {
      case 'schedule-session':
      case 'reporting-results':
      case 'ready':
      case 'loaded':
      case 'completing':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
        return this.state.segmentResource.payload.showCompletionCta;
      case 'quiz-failed':
      case 'advancing':
      case 'processing-request':
      case 'advancing-to':
      case 'waiting':
      case 'loading':
      case 'error':
        return false;
    }
  }

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'quiz-failed':
      case 'schedule-session':
      case 'reporting-results':
      case 'ready':
      case 'processing-request':
      case 'loaded':
      case 'completing':
      case 'advancing':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing': {
        return this.state.segmentResource.links;
      }
      case 'loading':
      case 'advancing-to': {
        return [this.state.link];
      }
      case 'waiting':
      case 'error':
        return [];
    }
  }

  @computed
  get confirmationModal(): Maybe<ConfirmationModal> {
    return this.segmentResource.andThen((r) => r.payload.confirmationModal);
  }

  @computed
  get segmentAdvancer(): AnyAdvancer {
    return this.segmentResource
      .map((segmentResource) => {
        switch (segmentResource.payload.type) {
          case 'overview':
            return submitAndAdvancer(this);
          case 'presentation':
          case 'external-program':
          case 'scorm':
          case 'team-discussion':
          case 'lecture':
          case 'survey':
          case 'assignment-due':
            return this.completeAndAdvancable
              .map<AnyAdvancer>(completeAndAdvancer)
              .orElse(() => this.advanceable.map<AnyAdvancer>(advancer))
              .orElse(() => advancerForResource(segmentResource))
              .getOrElse(noAdvancer);
        }
      })
      .getOrElse(noAdvancer);
  }

  @computed
  get tokenSegmentAdvancer(): AnyAdvancer {
    return this.tokenCompleteAndAdvancable
      .map<AnyAdvancer>(completeAndAdvancer)
      .orElse(() => this.advanceable.map<AnyAdvancer>(advancer))
      .orElse(() => this.segmentResource.andThen(advancerForResource))
      .getOrElse(noAdvancer);
  }

  @computed
  get tokenCompleteAndAdvancable(): Maybe<CompleteAndAdvancer> {
    return segmentTypeCompleteAndAdvancer(this);
  }

  @computed
  get completeAndAdvancable(): Maybe<CompleteAndAdvancer> {
    if (!this.showCompletionCta) return nothing();
    return segmentTypeCompleteAndAdvancer(this);
  }

  @computed
  get advanceable(): Maybe<Advancer> {
    return this.segmentResource.cata({
      Just: (segmentResource: SegmentResource) => {
        switch (segmentResource.payload.type) {
          case 'assignment-due':
          case 'survey':
            return nothing();
          case 'overview':
          case 'lecture':
          case 'external-program':
          case 'scorm':
          case 'presentation': {
            return findLinkBy({ rel: 'next' }, this.links).cata({
              Just: () => {
                // Overview segments can always advance
                if (overviewSegment(segmentResource)) {
                  return just(advancer(this));
                }

                // SCORM playback: advance is gated by +completability+ from
                // the backend, which already folds in +require_completion+.
                // +completable+ → allow advance (either the flag is off, or
                // tracking says the package reported completion).
                // +not-completable+ → hide the plain "Next Segment" button
                // too, so setting +require_completion+ actually blocks
                // forward navigation, not just the Complete-and-advance
                // path.
                if (segmentResource.payload.type === 'scorm') {
                  return segmentResource.payload.completability === 'not-completable'
                    ? nothing()
                    : just(advancer(this));
                }

                return segmentComplete(segmentResource).andThen(() => just(advancer(this)));
              },
              Nothing: () => nothing(),
            });
          }
          case 'team-discussion':
            return nothing();
        }
      },
      Nothing: () => {
        return nothing();
      },
    });
  }

  @computed
  get attachments(): AttachmentResource[] {
    return this.segmentResource.map((sr) => sr.payload.attachments).getOrElseValue([]);
  }

  @computed
  get results(): Maybe<JsonValue> {
    return this.segmentResource.cata({
      Just: (segmentResource: SegmentResource) => {
        switch (segmentResource.payload.type) {
          case 'presentation':
          case 'overview':
            return just(segmentResource.payload.results);
          case 'assignment-due':
          case 'team-discussion':
          case 'external-program':
          case 'scorm':
          case 'lecture':
          case 'survey':
            return nothing();
        }
      },
      Nothing: () => {
        return nothing();
      },
    });
  }

  @computed
  get embeddedFormFieldAssets(): Maybe<FormFieldAssetResource[]> {
    return this.segmentResource.cata({
      Just: (segmentResource: SegmentResource) => {
        switch (segmentResource.payload.type) {
          case 'overview':
            return just(segmentResource.payload.embeddedFormFieldAssets);
          case 'presentation':
          case 'assignment-due':
          case 'team-discussion':
          case 'external-program':
          case 'scorm':
          case 'lecture':
          case 'survey':
            return nothing();
        }
      },
      Nothing: () => {
        return nothing();
      },
    });
  }

  @computed
  get allFieldsAreComplete(): boolean {
    switch (this.state.kind) {
      case 'ready':
      case 'processing-request':
        return this.embeddedFormFieldOutputs.every((field) => {
          return field.isRequired ? field.isValid : true;
        });
      case 'quiz-failed':
      case 'schedule-session':
      case 'reporting-results':
      case 'loaded':
      case 'completing':
      case 'completing-and-advancing':
      case 'submitting-and-advancing':
      case 'previous-advancing':
      case 'advancing':
      case 'advancing-to':
      case 'waiting':
      case 'loading':
      case 'error':
        return false;
    }
  }

  @computed
  get sessionScheduled(): boolean {
    return this.segmentResource.cata({
      Just: (segmentResource: SegmentResource) => {
        switch (segmentResource.payload.type) {
          case 'overview':
            return segmentResource.payload.sessionScheduled;
          case 'presentation':
          case 'assignment-due':
          case 'team-discussion':
          case 'external-program':
          case 'scorm':
          case 'lecture':
          case 'survey':
            return false;
        }
      },
      Nothing: () => {
        return false;
      },
    });
  }

  @computed
  get isFreebusyCoaching(): boolean {
    return this.segmentResource.cata({
      Just: (segmentResource: SegmentResource) => {
        switch (segmentResource.payload.type) {
          case 'overview':
            return segmentResource.payload.isFreebusyCoaching;
          case 'presentation':
          case 'assignment-due':
          case 'team-discussion':
          case 'external-program':
          case 'scorm':
          case 'lecture':
          case 'survey':
            return false;
        }
      },
      Nothing: () => {
        return false;
      },
    });
  }

  @computed
  get enclosureOrigin(): Maybe<string> {
    return this.enclosureLink
      .get()
      .andThen(({ href }) => toUrl(href))
      .map(({ origin }) => origin);
  }

  @computed
  get uponCompletion(): Maybe<UponCompletion> {
    return this.segmentResource.cata({
      Just: (segmentResource: SegmentResource) => {
        switch (segmentResource.payload.type) {
          case 'assignment-due':
          case 'external-program':
          case 'scorm':
          case 'lecture':
          case 'presentation':
          case 'survey':
          case 'team-discussion':
            return nothing();
          case 'overview':
            return segmentResource.payload.uponCompletion;
        }
      },
      Nothing: () => nothing(),
    });
  }
}

export default SegmentStore;
